import { ContentHeader } from "../components/ContentHeader";
import { useApi } from "../hooks/useApi";

// ---- Response shapes (local — not shared via api/types.ts) ----
interface JvmService {
  app: string;
  heap_pct: number;
  gc_rate: number;
  hikari_pending: number;
}
interface JvmResponse {
  status: "ok" | "unavailable";
  reason?: string;
  services: JvmService[];
}
interface KafkaHealth {
  offline_partitions: number;
  active_controllers: number;
  healthy: boolean;
}
interface MessagingResponse {
  status: "ok" | "unavailable";
  reason?: string;
  kafka: KafkaHealth | null;
}

const TITLE = "Runtime & Queues";
const DESC = "JVM heap, GC & connection-pool health plus Kafka cluster state from Prometheus.";

// green <70, amber 70-90, red >90 — matches InfraTab thresholds.
function heapColor(pct: number): string {
  if (pct > 90) return "var(--red)";
  if (pct >= 70) return "var(--yellow)";
  return "var(--green)";
}

export function RuntimeTab() {
  const jvmR = useApi<JvmResponse>("/command-center/runtime/jvm");
  const msgR = useApi<MessagingResponse>("/command-center/runtime/messaging");

  const loading = jvmR.loading || msgR.loading;
  const error = jvmR.error || msgR.error;

  if (loading) {
    return (
      <>
        <ContentHeader title={TITLE} description={DESC} />
        <div className="loading"><div className="loading-spinner" /><p>Querying runtime metrics…</p></div>
      </>
    );
  }
  if (error || !jvmR.data || !msgR.data) {
    return (
      <>
        <ContentHeader title={TITLE} description={DESC} />
        <div className="empty">Could not load runtime metrics. Is command-center-api running?</div>
      </>
    );
  }

  const unavailable = jvmR.data.status === "unavailable" || msgR.data.status === "unavailable";
  if (unavailable) {
    return (
      <>
        <ContentHeader title={TITLE} description={DESC} />
        <div className="empty">
          Live runtime metrics need <code>GRAFANA_TOKEN</code> on the API to query Prometheus.
          {(jvmR.data.reason || msgR.data.reason) && (
            <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-muted)" }}>
              {jvmR.data.reason || msgR.data.reason}
            </div>
          )}
        </div>
      </>
    );
  }

  const services = [...(jvmR.data.services ?? [])].sort(
    (a, b) => b.heap_pct - a.heap_pct || a.app.localeCompare(b.app));
  const kafka = msgR.data.kafka;

  return (
    <>
      <ContentHeader title={TITLE} count={services.length} description={DESC} />

      <div className="cov-summary">
        {kafka == null ? (
          <span className="cov-pill muted"><span className="dot" />Kafka metrics not scraped</span>
        ) : (
          <>
            <span className={`cov-pill ${kafka.healthy ? "ok" : "crit"}`}>
              <span className="dot" />Kafka {kafka.healthy ? "healthy" : "degraded"}
            </span>
            <span className={`cov-pill ${kafka.offline_partitions > 0 ? "crit" : "ok"}`}>
              <span className="dot" /><strong>{kafka.offline_partitions}</strong> offline partitions
            </span>
            <span className={`cov-pill ${kafka.active_controllers === 1 ? "ok" : "crit"}`}>
              <span className="dot" /><strong>{kafka.active_controllers}</strong> active controller{kafka.active_controllers === 1 ? "" : "s"}
            </span>
          </>
        )}
      </div>

      {services.length === 0 ? (
        <div className="empty">No JVM metrics — Micrometer endpoints may not be scraped.</div>
      ) : (
        <table className="cov-matrix">
          <thead>
            <tr>
              <th>Service</th>
              <th>Heap %</th>
              <th>GC rate (s/s)</th>
              <th>Hikari pending</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.app}>
                <td><code style={{ fontFamily: "var(--font-mono)" }}>{s.app}</code></td>
                <td className="num" style={{ color: heapColor(s.heap_pct) }}>{s.heap_pct}%</td>
                <td className="num">{s.gc_rate}</td>
                <td className={`cov-cell ${s.hikari_pending > 0 ? "no" : "yes"}`}>{s.hikari_pending}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export default RuntimeTab;
